// Sección (Restaurante | Mercado) de cada línea del carrito y agrupación para mostrarlas.
// MÓDULO PURO: sin React ni I/O.
import type { CartItem } from "@/components/microsite/types"
import type { StorefrontSection } from "@/lib/microsite/storefrontKind"

export type CartGroup<L> = { section: StorefrontSection | null; lines: L[] }

/**
 * Marca explícita gana (`isRetail`); sin marca, se infiere contra la lista COMPLETA de platos
 * (`dishIds`, nunca las previas de pasillo): si el id es un plato → restaurante, si no → mercado.
 */
export function cartLineSection(
  line: Pick<CartItem, "itemId" | "isRetail">,
  dishIds: ReadonlySet<string>,
): StorefrontSection {
  if (line.isRetail === true) return "market"
  if (line.isRetail === false) return "restaurant"
  return dishIds.has(line.itemId) ? "restaurant" : "market"
}

/**
 * Sello al agregar (regla única, F6): producto del mercado → `isRetail: true`; plato de la
 * lista → `isRetail: false`; ni uno ni otro → la MISMA línea sin marca (se infiere después
 * con `cartLineSection`). La marca es solo-cliente: el servidor sigue leyendo `items.is_retail`.
 */
export function sealCartLineSection<L extends Pick<CartItem, "itemId" | "isRetail">>(
  line: L,
  ids: { retailIds: ReadonlySet<string>; dishIds: ReadonlySet<string> },
): L {
  if (ids.retailIds.has(line.itemId)) return { ...line, isRetail: true }
  if (ids.dishIds.has(line.itemId)) return { ...line, isRetail: false }
  return line
}

/** Un solo tipo (o vacío) → un grupo sin título; los dos → [restaurante, mercado], cada uno en el orden del carrito. */
export function groupCartLines<L>(
  cart: readonly L[],
  sectionOf: (line: L) => StorefrontSection,
): CartGroup<L>[] {
  const restaurant: L[] = []
  const market: L[] = []
  for (const line of cart) (sectionOf(line) === "market" ? market : restaurant).push(line)
  if (restaurant.length === 0 || market.length === 0) return [{ section: null, lines: [...cart] }]
  return [
    { section: "restaurant", lines: restaurant },
    { section: "market", lines: market },
  ]
}
